import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Col, Row, Button, Badge, Modal, Form } from "react-bootstrap";
import { FaPencilAlt, FaRegTrashAlt, FaEye } from "react-icons/fa";
import {
  fetchVendorProfiles,
  updateVendorProfile,
  clearError,
  clearSuccess,
} from "../../redux/slices/vendorProfileSlice";
import InfoPill from "../Common/InfoPill";
import { toast } from "react-toastify";

const Vendors = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const newId = queryParams.get("newId");

  // Added By Farhan Khan on 05-May-2026
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [activeAction, setActiveAction] = useState({ id: null, status: null });
  const [remarkError, setRemarkError] = useState("");
  // End Here By Farhan Khan on 05-May-2026

  const {
    vendorProfiles: vendors,
    isLoading,
    error,
    success,
    isUpdating,
  } = useSelector((state) => state.vendorProfile);

  useEffect(() => {
    dispatch(fetchVendorProfiles({ id: newId }));
  }, [dispatch, newId]);

  // Show toast messages for success and error
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const handleCreateVendor = () => {
    navigate("/admin/vendors/create");
  };

  // Added By Farhan Khan on 05-May-2026
  const handleStatusUpdate = (id, newStatus, description = "") => {
    dispatch(updateVendorProfile({ id, vendorProfileData: { status: newStatus, business_description: description } }))
      .unwrap()
      .then(() => {
        dispatch(fetchVendorProfiles({ id: newId }));
      })
      .catch((err) => console.error("Status update failed:", err));
  };

  const handleActionClick = (id, status) => {
    if (status === "Approved") {
      handleStatusUpdate(id, status);
    } else {
      setActiveAction({ id, status });
      setRemarkText("");
      setRemarkError("");
      setShowRemarkModal(true);
    }
  };

  const handleSaveRemark = () => {
    if (!remarkText.trim()) {
      setRemarkError("Remark is required.");
      return;
    }
    handleStatusUpdate(activeAction.id, activeAction.status, remarkText);
    setShowRemarkModal(false);
  };
  // End Here By Farhan Khan on 05-May-2026

  // Multiple vendors are now allowed - removed single vendor restriction

  // Table headers
  const headers = [
    // {
    //   prop: "image_url",
    //   title: "Profile Image",
    //   cell: (row) => (
    //     <div
    //       style={{
    //         width: 40,
    //         height: 40,
    //         borderRadius: 4,
    //         overflow: "hidden",
    //         background: "#f1f1f1",
    //       }}
    //     >
    //       {row.image_url && (
    //         <img
    //           alt="vendor"
    //           src={row.image_url || "./not-img-found.png"}
    //           style={{ width: "100%", height: "100%", objectFit: "cover" }}
    //           onError={(e) => {
    //             e.currentTarget.src = "/not-img-found.png";
    //           }}
    //         />
    //       )}
    //     </div>
    //   ),
    // },
    {
      prop: "business_name",
      title: "Business Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Link
          to={`/admin/vendors/details/${row.id}`}
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
        >
          {row.business_name}
        </Link>
      ),
    },
    {
      prop: "contact_person",
      title: "Contact Person",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "business_email",
      title: "Email",
      isSortable: true,
      isFilterable: true,
    },
    { prop: "business_phone", title: "Phone", isFilterable: true },
    { prop: "gst_no", title: "GST No", isSortable: true, isFilterable: true },
    // {
    //   prop: "payment_terms",
    //   title: "Payment Terms",
    //   isSortable: true,
    //   isFilterable: true,
    // },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        let status = row.status || "Under-Review";

        // Standardize status values
        if (status === "Active" || status === "Approve") status = "Approved";
        if (status === "In-Active" || status === "Pending") status = "Under-Review";
        if (status === "Reject") status = "Rejected";

        let customStyle = {
          padding: "6px 14px",
          borderRadius: "50rem",
          fontWeight: "600",
          fontSize: "0.85rem",
          letterSpacing: "0.3px",
          display: "inline-block",
          textAlign: "center",
          whiteSpace: "nowrap"
        };

        // Apply subtle UI tones
        if (status === "Approved") {
          customStyle = { ...customStyle, backgroundColor: "#d1fae5", color: "#065f46", border: "1px solid #34d399" };
        } else if (status === "Rejected") {
          customStyle = { ...customStyle, backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #f87171" };
        } else if (status === "Hold") {
          customStyle = { ...customStyle, backgroundColor: "#fef3c7", color: "#d97706", border: "1px solid #fbbf24" };
        } else {
          status = "Under-Review";
          customStyle = { ...customStyle, backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" };
        }

        return (
          <span style={customStyle}>
            {status}
          </span>
        );
      },
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2"  >
          {/* Added By Farhan Khan on 05-May-2026 */}
          {row.status !== "Approved" && (

            <Button
              size="sm"
              variant="outline-success"
              onClick={() => handleActionClick(row.id, "Approved")}
              disabled={isUpdating}
              style={{ borderRadius: "6px", fontWeight: "500", transition: "all 0.2s", whiteSpace: "nowrap" }}
            >
              Approve
            </Button>
          )}
          {row.status !== "Rejected" && (

            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleActionClick(row.id, "Rejected")}
              disabled={isUpdating}
              className="w-auto"
              style={{ borderRadius: "6px", fontWeight: "500", transition: "all 0.2s", whiteSpace: "nowrap" }}
            >
              Reject
            </Button>
          )}
          {row.status !== "Hold" && (
            <Button
              size="sm"
              onClick={() => handleActionClick(row.id, "Hold")}
              disabled={isUpdating}
              style={{
                borderRadius: "6px",
                // fontWeight: "500",
                backgroundColor: "#6c757d",
                borderColor: "#6c757d",
                color: "#fff",
                transition: "all 0.2s",
                whiteSpace: "nowrap"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#e96b0c";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#fd7e14";
              }}
            >
              Hold
            </Button>
          )}
          {/* <Button
            size="sm"
            variant="outline-primary"
            onClick={() => navigate(`/admin/vendors/edit/${row.id}`)}
            disabled={isUpdating}
            className="d-flex align-items-center justify-content-center"
            style={{ borderRadius: "6px", fontWeight: "500", gap: "6px", transition: "all 0.2s", whiteSpace: "nowrap" }}
          >
            <FaPencilAlt size={12} />
          </Button> */}
          {/* End Here By Farhan Khan on 05-May-2026 */}
        </div>

      ),
    },
  ];

  if (isLoading) {
    return <div className="text-center mt-4">Loading vendors...</div>;
  }

  // Handle case where vendors data is undefined or null
  if (!vendors) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Link className="breadcrum" to="/">
            Home{"/"}
            <i
              className="fa-solid fa-chevron-right"
              style={{ marginLeft: "1px", marginRight: "1px" }}
            ></i>{" "}
            Vendors
          </Link>
        </div>
        <div className="text-center mt-4">
          <p>No vendors data available. Please try again later.</p>
          <Button
            onClick={() => dispatch(fetchVendorProfiles())}
            variant="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const filteredVendors = newId
    ? vendors.filter((v) => String(v.id) === String(newId))
    : vendors;

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Vendors
        </Link>
      </div>

      <DatatableWrapper
        body={filteredVendors}
        headers={headers}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search vendors..."
                className="w-100 btn-success"
              />
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill
              left={newId ? "Created Vendor" : "Total Vendors"}
              right={filteredVendors?.length || 0}
            />
            {newId && (
              <Button
                variant="outline-primary"
                size="sm"
                className="ms-2"
                onClick={() => navigate("/admin/vendors")}
              >
                Show All
              </Button>
            )}
          </div>
          <div className="d-flex align-items-center">
            <Button
              onClick={handleCreateVendor}
              className="btn-sm custom-btn"
            >
              New Vendor
            </Button>
          </div>
        </div>

        {/* Show message when vendor already exists */}
        {/* {vendorExists && (
          <div className="alert alert-info mb-3" role="alert">
            <i className="fa-solid fa-info-circle me-2"></i>
            Only one vendor profile is allowed. You can edit the existing vendor
            profile.
          </div>
        )} */}

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",

          }} />
        </div>
      </DatatableWrapper>
      {/* Added By Farhan Khan on 05-May-2026 */}
      <Modal show={showRemarkModal} onHide={() => setShowRemarkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Remark</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Reason for {activeAction.status === "Hold" ? "putting on hold" : "rejection"} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={remarkText}
              onChange={(e) => {
                setRemarkText(e.target.value);
                if (e.target.value.trim()) setRemarkError("");
              }}
              isInvalid={!!remarkError}
              placeholder="Enter your remarks here..."
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {remarkError}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemarkModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRemark} disabled={isUpdating}>
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* End Here By Farhan Khan on 05-May-2026 */}
    </div>
  );
};

export default Vendors;
